import { useEffect, useReducer } from "react";

import { IAccountDetails } from "@/Common/Components/Portal/InvitationPopup/AccountDetails/AccountDetails";
import { getAccountDetails } from "@/Common/Components/Portal/InvitationPopup/Api/InvitationApiClient";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import * as LocalOperations from "@/Common/Operations/operations.local";

interface AccountDetailsHookOptions {
    enabled?: boolean;
    accountStatus: PortalRegistrationState;
}

type AccountDetailsActions =
    | { type: "FETCH_SUCCESS"; payload: IAccountDetails }
    | { type: "FETCH_ERROR"; payload: string }
    | { type: "LOADING"; payload: boolean };

interface AccountDetailsState {
    details?: IAccountDetails;
    error: null | string;
    loading: boolean;
}

const initialState: AccountDetailsState = {
    details: {
        username: "",
        status: PortalRegistrationState.Default,
        refusalDate: new Date(),
        lastLoginDate: new Date(),
        lastInvitationDate: new Date(),
        lastLoginAttemptDate: new Date()
    },
    error: null,
    loading: false
};

function reducer(state: typeof initialState, action: AccountDetailsActions): AccountDetailsState {
    switch (action.type) {
        case "FETCH_SUCCESS":
            return { details: action.payload, error: null, loading: false };
        case "FETCH_ERROR":
            return { details: undefined, error: action.payload, loading: false };
        case "LOADING":
            return { ...state, loading: action.payload };
        default:
            return initialState;
    }
}

const useAccountDetails = (contactId: string, options?: AccountDetailsHookOptions): AccountDetailsState => {
    const { enabled = false, accountStatus } = options || {};
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(
        () => {
            if (enabled) {
                dispatch({ type: "LOADING", payload: true });

                getAccountDetails(contactId)
                    .then(AccountDetails => {
                        const { Username, AccountStatus, LastLoginDate, LastInvitationDate, LastLoginAttemptDate, RefusalDate } = AccountDetails;

                        dispatch({
                            type: "FETCH_SUCCESS",
                            payload: {
                                status: AccountStatus,
                                username: Username,
                                refusalDate: new Date(RefusalDate),
                                lastLoginDate: new Date(LastLoginDate),
                                lastInvitationDate: new Date(LastInvitationDate),
                                lastLoginAttemptDate: new Date(LastLoginAttemptDate)
                            }
                        });

                        sessionStore.dispatchAsync(
                            LocalOperations.Portal.Portal_UpdateState,
                            {
                                patientContactId: contactId,
                                portalStatus: AccountStatus
                            }
                        );
                    })
                    .catch((error: Error) => {
                        dispatch({ type: "FETCH_ERROR", payload: error.message || "" });
                    })
                    .finally(() => dispatch({ type: "LOADING", payload: false }));
            }
        },
        [accountStatus, enabled, contactId]
    );

    return state;
};

export default useAccountDetails;
